import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, ArrowLeft, Route, ShieldCheck, Clock, MapPin, Phone } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ y: 40, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const FACTORS = [
  { icon: Route, title: "Distance & Route", desc: "Mileage between your old door and new door is the biggest factor. We quote flat — fuel, tolls, and drive time included." },
  { icon: Truck, title: "Load Size", desc: "A studio apartment and a five-bedroom home move differently. Your inventory list drives the truck size and crew count." },
  { icon: Clock, title: "Delivery Window", desc: "Dedicated trucks mean your dates, not a carrier's. We agree on pickup and delivery windows before we load a single box." },
  { icon: ShieldCheck, title: "Protection Level", desc: "Every item is wrapped and strapped as standard. Full-value coverage options are available for high-value shipments." },
];

export default function LongDistance() {
  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="long-distance-page">
      <div className="noise-overlay" />
      <header className="bg-[#F4F4F5]/95 backdrop-blur-md border-b border-zinc-300 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="ld-logo" className="flex items-center gap-3">
            <img src="/logo192.png" alt="Anchor Moving logo" className="w-9 h-9 object-cover" />
            <span className="font-display uppercase text-xl tracking-wide leading-none">Anchor Moving</span>
          </Link>
          <Link
            to="/"
            data-testid="ld-back-link"
            className="inline-flex items-center gap-2 font-sub text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 hover:text-[#0057FF] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-20 sm:py-28">
        <Reveal>
          <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF]">Interstate & Long Distance Movers</p>
          <h1 className="font-display uppercase text-4xl sm:text-6xl leading-[0.9] tracking-tight mt-4 max-w-4xl" data-testid="ld-heading">
            Lebanon, TN To <span className="text-[#0057FF]">Anywhere In America.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-600 text-base md:text-lg leading-relaxed">
            Anchor Moving runs long-distance and interstate moves from our home base in Lebanon, Tennessee.
            Dedicated trucks, GPS-tracked loads, and guaranteed delivery windows — your belongings never share
            a trailer with a stranger's, and they never sit in a warehouse waiting for a second carrier.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-300 border border-zinc-300" data-testid="ld-factors">
          {FACTORS.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08} className="bg-white">
              <div className="p-8">
                <f.icon className="w-6 h-6 text-[#0057FF]" strokeWidth={2} />
                <h2 className="font-display uppercase text-xl mt-4">{f.title}</h2>
                <p className="text-sm text-zinc-600 leading-relaxed mt-3">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-16 bg-zinc-900 text-white p-10 sm:p-14 shadow-[8px_8px_0px_0px_#0057FF]" data-testid="ld-process">
          <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF]">How An Interstate Move Works</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              ["01", "We survey", "Call or send a quote request. We build your inventory, map the route, and give you one flat number."],
              ["02", "We load direct", "Your crew loads your dedicated truck, seals it, and drives straight through — no terminals, no transfers."],
              ["03", "We place everything", "On delivery day we unload, place every piece where you want it, and walk the home with you before leaving."],
            ].map(([num, title, desc]) => (
              <div key={num}>
                <span className="font-display text-4xl text-[#0057FF]">{num}</span>
                <h3 className="font-display uppercase text-xl mt-2">{title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-2">{desc}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-16 flex flex-wrap items-center gap-6">
          <Link to="/#booking" data-testid="ld-book-button">
            <span className="inline-flex items-center gap-2 bg-[#0057FF] text-white font-sub font-bold uppercase tracking-[0.15em] text-sm px-8 py-4 rounded-none shadow-[4px_4px_0px_0px_#09090B] transition-[transform,box-shadow] duration-150 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[7px_7px_0px_0px_#09090B]">
              Book A Long Distance Move
            </span>
          </Link>
          <a href="tel:+12702255228" data-testid="ld-call-link" className="inline-flex items-center gap-3 font-sub font-bold text-lg text-zinc-900 hover:text-[#0057FF] transition-colors">
            <Phone className="w-5 h-5" /> 270-225-5228
          </a>
          <span className="inline-flex items-center gap-2 text-sm text-zinc-500">
            <MapPin className="w-4 h-4 text-[#0057FF]" /> Lebanon, Tennessee — serving the entire United States
          </span>
        </Reveal>
      </main>
    </div>
  );
}
